import React, { useEffect, useState } from "react";
import { library } from "@fortawesome/fontawesome-svg-core";
import "fontsource-open-sans";
import { Helmet } from "react-helmet";
import { LanguageProvider } from "../../contexts/language-context";
import { PageUtagDataProvider } from "../../contexts/page-utag-data-context";
import TealiumScripts from "../tealium/tealium-scripts";

import ExternalModal from "../external-modal/external-modal";
import favicon from "../../images/favicons/favicon-32x32.png";

// NOTE: only import the icons we actually need to use, do not import the entire library.
// Also please keep the icon names in alphabetical order so that it is easier maintain
// FAS (solid)
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCheck,
  faPlus,
  faSortUp
} from "@fortawesome/free-solid-svg-icons";

// FAL (light)
import {
  faAnalytics,
  faArrowsAlt,
  faArrowLeft,
  faArrowRight,
  faArrowDown,
  faArrowToBottom,
  faArrowToTop,
  faAt,
  faBars,
  faBoxCheck,
  faBriefcase,
  faBuilding,
  faCalculator,
  faCalendarAlt,
  faChartLine,
  faChevronCircleLeft,
  faChevronCircleRight,
  faChevronDoubleDown,
  faChevronDoubleUp,
  faCity,
  faClipboardCheck,
  faClock,
  faCommentDollar,
  faCreditCard,
  faDesktop,
  faDollarSign,
  faDoorOpen,
  faEnvelope,
  faEnvelopeOpenDollar,
  faEnvelopeOpenText,
  faExternalLink,
  faEye,
  faEyeSlash,
  faFax,
  faGlobe,
  faGraduationCap,
  faHandHoldingUsd,
  faHeartbeat,
  faFileAlt,
  faHandshake,
  faHammer,
  faHandHoldingHeart,
  faHome,
  faHouseUser,
  faLandmark,
  faLock,
  faMapMarker,
  faMapMarkerAlt,
  faMapPin,
  faMobile,
  faMobileAlt,
  faMoneyBillWave,
  faMoneyCheck,
  faPaintRoller,
  faPercent,
  faPhone,
  faPhoneAlt,
  faPiggyBank,
  faPlay,
  faPlus as faPlusLight,
  faPlusCircle,
  faPrint,
  faQuestion,
  faQuestionCircle,
  faSearch,
  faShieldCheck,
  faShoppingCart,
  faShovel,
  faSortAlt,
  faSun,
  faTools,
  faTv,
  faUmbrellaBeach,
  faUniversity,
  faUser,
  faUserFriends,
  faUsers,
  faWheelchair
} from "@fortawesome/pro-light-svg-icons";
// FAB (social)
import { faFacebookF, faLinkedinIn, faTwitter, faYoutube, faInstagram } from "@fortawesome/free-brands-svg-icons";
import "@fortawesome/fontawesome-svg-core/styles.css";
import "./global-layout.scss";
import DetectBrowser from "../../helpers/detectBrowser";
import GtagScript from "../gtag-logic-script";

// NOTE: please copy and paste the imported icons above and paste here to keep the library consistent.
library.add(
  // FAS (solid)
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faArrowDown,
  faChevronUp,
  faCheck,
  faPlus,
  // FAL (light)
  faAnalytics,
  faArrowsAlt,
  faArrowLeft,
  faArrowRight,
  faArrowToBottom,
  faArrowToTop,
  faAt,
  faBars,
  faBoxCheck,
  faBriefcase,
  faBuilding,
  faCalculator,
  faCalendarAlt,
  faChartLine,
  faChevronCircleLeft,
  faChevronCircleRight,
  faChevronDoubleDown,
  faChevronDoubleUp,
  faCity,
  faClipboardCheck,
  faClock,
  faCommentDollar,
  faCreditCard,
  faDesktop,
  faDollarSign,
  faDoorOpen,
  faEnvelope,
  faEnvelopeOpenDollar,
  faEnvelopeOpenText,
  faExternalLink,
  faEye,
  faEyeSlash,
  faFax,
  faGlobe,
  faGraduationCap,
  faHandHoldingUsd,
  faHandshake,
  faHeartbeat,
  faFileAlt,
  faHammer,
  faHandHoldingHeart,
  faHome,
  faHouseUser,
  faLandmark,
  faLock,
  faMapMarker,
  faMapMarkerAlt,
  faMapPin,
  faMobile,
  faMobileAlt,
  faMoneyBillWave,
  faMoneyCheck,
  faPaintRoller,
  faPercent,
  faPhone,
  faPhoneAlt,
  faPiggyBank,
  faPlay,
  faPlusLight,
  faPlusCircle,
  faPrint,
  faQuestion,
  faQuestionCircle,
  faSearch,
  faShieldCheck,
  faShoppingCart,
  faShovel,
  faSortAlt,
  faSun,
  faTools,
  faTv,
  faUmbrellaBeach,
  faUniversity,
  faUser,
  faUserFriends,
  faUsers,
  faWheelchair,
  // FAB (social)
  faFacebookF,
  faLinkedinIn,
  faTwitter,
  faYoutube,
  faInstagram,
  faSortUp
);

const allowlist = [
  "wafd",
  "washingtonfed",
  "mailto:",
  "tel:",
  "javascript",
  "vimeo",
  "maps.google.com",
  "timetrade",
  "washington-federal",
  "washingtionfederal",
  "wafedweb",
  "washfed",
  "www.ezcardinfo.com",
  "myaccountaccess.com",
  "ezapprove.fnis.com"
];

const isUrlExternalLink = function (url) {
  if (!url) {
    return null;
  }
  // if the url contains the actual host (wafdbank.com or localhost) then it is NOT an external link
  if (window && url.indexOf(window.location.host) >= 0) {
    return false;
  }

  let whiteListed = false;
  allowlist.forEach((text) => {
    if (url.indexOf(text) >= 0) {
      //console.log("url ["+ url +"] is on the whiteList");
      whiteListed = true;
      return false; // does nothing
    }
  });
  // not whiteListed, AND not a relative path (first char is "/"), AND does start with http or https
  return !whiteListed && url.indexOf("/") !== 0 && (url.indexOf("http://") === 0 || url.indexOf("https://") === 0);
};

let GlobalLayout = (props) => {
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const [showExtLinkModal, setShowExtLinkModal] = useState(false);
  const [url, setUrl] = useState("/");
  const handleClose = () => setShowExtLinkModal(false);
  const handleShow = (e) => {
    e.preventDefault();
    setUrl(e.target.href);
    setShowExtLinkModal(true);
  };

  useEffect(() => {
    setShowBrowserModal(true); // init on page load (this has nothing to do with external links)
    // check all links on page for external to pop modal
    let anchors = document.getElementsByTagName("a");
    for (const anchor of anchors) {
      if (anchor.href && typeof anchor.href === "string" && isUrlExternalLink(anchor.href)) {
        //console.log("adding event handler for external link: " + anchor.href);
        anchor.addEventListener("click", handleShow);
      }
    }
  }, []);

  const extLinkModalData = {
    handleClose,
    show: showExtLinkModal,
    url
  };

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/png" sizes="32x32" href={favicon} />
      </Helmet>
      <PageUtagDataProvider>
        <GtagScript/>
        <TealiumScripts />
        <LanguageProvider>
          {showBrowserModal && <DetectBrowser />}
          {showExtLinkModal && <ExternalModal {...extLinkModalData} />}
          {props.children}
        </LanguageProvider>
      </PageUtagDataProvider>
    </>
  );
};
export default GlobalLayout;
